import { motion, AnimatePresence } from 'framer-motion';
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

import { useAppState } from '../../../Context/AppStateContext';
import { theme } from '../../../theme';

// Subtle scan line animation for terminal effect - slowed down and reduced intensity
const scanLines = keyframes`
  0% { background-position: 0 0; }
  100% { background-position: 0 60px; }
`;

// Subtle pulse animation for active items - smoother and less intense
const pulseEffect = keyframes`
  0% { border-color: rgba(31, 207, 94, 0.5); }
  50% { border-color: rgba(31, 207, 94, 0.8); }
  100% { border-color: rgba(31, 207, 94, 0.5); }
`;

// Removed text flicker animation as it was causing flickering

const TaskbarContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 42px; /* Set a consistent base height */
  min-height: 42px; /* Ensure minimum height is maintained */
  background: linear-gradient(to bottom, rgba(10, 20, 10, 0.9), rgba(0, 0, 0, 0.95));
  border-top: 1px solid ${theme.colors.green};
  display: flex;
  align-items: center;
  padding: 5px 10px;
  z-index: 9999;
  backdrop-filter: blur(10px);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.7);
  overflow: hidden; /* Prevent vertical scrollbar */

  @media (max-width: 600px) {
    min-height: 46px;
    height: 46px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
    background-image: linear-gradient(
      0deg,
      transparent 0%,
      rgba(32, 128, 32, 0.02) 50%,
      transparent 100%
    );
    background-size: 100% 3px;
    animation: ${scanLines} 20s linear infinite; /* Slowed down animation */
    pointer-events: none;
    opacity: 0.1; /* Reduced opacity */
    will-change: background-position; /* Performance optimization */
  }

  &::after {
    content: '';
    position: absolute;
    top: 1px;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      to right,
      transparent,
      rgba(31, 207, 94, 0.3),
      rgba(31, 207, 94, 0.5),
      rgba(31, 207, 94, 0.3),
      transparent
    );
  }
`;

const TaskbarItems = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  height: 32px; /* Fixed height to match items */
  overflow-x: auto;
  overflow-y: hidden; /* Prevent vertical scrollbar */
  padding: 0 5px;
  justify-content: flex-start;

  /* Add a subtle visual indicator when empty */
  &:empty::before {
    content: '';
    width: 32px;
    height: 3px;
    background-color: rgba(31, 207, 94, 0.2);
    border-radius: 1px;
    margin-right: auto;
    margin-left: auto;
    opacity: 0.5;
  }

  @media (max-width: 600px) {
    justify-content: flex-start;
    padding-left: 2px;
    height: 38px; /* Match the mobile item height */
  }

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    height: 3px;
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(31, 207, 94, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const TaskbarItem = styled(motion.div) <{ $color?: string; $active?: boolean }>`
  height: 32px;
  min-width: 160px;
  max-width: 200px;
  margin-right: 8px;
  padding: 0 12px;
  background: ${props =>
    props.$active
      ? `linear-gradient(to bottom, rgba(31, 207, 94, 0.15), rgba(31, 207, 94, 0.05))`
      : `linear-gradient(to bottom, rgba(0, 10, 0, 0.8), rgba(0, 0, 0, 0.9))`};
  border: 1px solid
    ${props => (props.$active ? props.$color || theme.colors.green : `rgba(31, 207, 94, 0.3)`)};
  border-radius: 3px;
  color: white;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition:
    background 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  box-shadow: ${props =>
    props.$active
      ? `inset 0 0 5px rgba(31, 207, 94, 0.2), 0 0 8px rgba(31, 207, 94, 0.2)`
      : `inset 0 1px 0 rgba(255, 255, 255, 0.05), 0 1px 3px rgba(0, 0, 0, 0.3)`};
  position: relative;
  will-change: transform, opacity; /* Performance optimization */
  flex-shrink: 0; /* Prevent items from shrinking */

  @media (max-width: 600px) {
    min-width: auto;
    max-width: auto;
    width: 38px;
    height: 38px;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 4px;
    border-radius: 3px;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1px;
    background: linear-gradient(to right, transparent, rgba(255, 255, 255, 0.1), transparent);
  }

  &:hover {
    background: linear-gradient(to bottom, rgba(31, 207, 94, 0.1), rgba(31, 207, 94, 0.05));
    border-color: ${props => (props.$color ? `${props.$color}` : theme.colors.green)};
    box-shadow: 0 0 8px rgba(31, 207, 94, 0.3);
  }

  &:active {
    background: linear-gradient(to bottom, rgba(31, 207, 94, 0.15), rgba(31, 207, 94, 0.05));
    box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
    transform: translateY(1px);
  }

  ${props =>
    props.$active &&
    `
    animation: ${pulseEffect} 3s infinite ease-in-out; /* Slowed down animation */
  `}

  .icon-container {
    margin-right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.$color || theme.colors.green};

    svg {
      font-size: 18px;
    }

    @media (max-width: 600px) {
      margin-right: 0;

      svg {
        font-size: 20px;
      }
    }
  }

  .title {
    font-size: 13px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    letter-spacing: 0.5px;
    color: ${theme.colors.green};

    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const Clock = styled.div`
  font-size: 13px;
  color: ${theme.colors.green};
  padding: 0 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex-shrink: 0; /* Prevent clock from shrinking */
  border-left: 1px solid rgba(31, 207, 94, 0.2);
  font-weight: 500;
  letter-spacing: 0.5px;
  position: relative;
  font-family: 'Courier New', monospace;

  @media (max-width: 600px) {
    padding: 0 8px;
    border-left: none;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    bottom: 8px;
    width: 1px;
    background: rgba(0, 0, 0, 0.5);

    @media (max-width: 600px) {
      display: none;
    }
  }

  .time {
    font-size: 14px;
    letter-spacing: 1px;

    @media (max-width: 600px) {
      font-size: 12px;
    }
  }

  .date {
    font-size: 10px;
    opacity: 0.8;
    text-align: center;
    margin-top: 2px;

    @media (max-width: 600px) {
      display: none;
    }
  }
`;

const Taskbar: React.FC = () => {
  const { minimizedWindows, isOnMobile } = useAppState();
  const [time, setTime] = useState<string>(getCurrentTime());
  const [date, setDate] = useState<string>(getCurrentDate());
  const [activeItem, setActiveItem] = useState<string | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const location = useLocation();

  function getCurrentTime() {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  function getCurrentDate() {
    const now = new Date();
    return now.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  }

  useEffect(() => {
    // Function to update the clock display
    const updateClock = () => {
      setTime(getCurrentTime());
      setDate(getCurrentDate());
    };

    // Update clock immediately on component mount
    updateClock();

    // Set up timer to sync with the start of the next minute
    const now = new Date();
    const nextMinute = new Date(now);
    nextMinute.setMinutes(now.getMinutes() + 1);
    nextMinute.setSeconds(0);
    nextMinute.setMilliseconds(0);
    const delay = nextMinute.getTime() - now.getTime();

    // We'll use regular variables instead of state to track timers
    let minuteTimer: NodeJS.Timeout | null = null;

    // Initial timeout to sync with minute boundary
    const initialSync = setTimeout(() => {
      // Update clock at the minute boundary
      updateClock();

      // Now set up interval for subsequent updates every minute
      minuteTimer = setInterval(updateClock, 60000);
    }, delay);

    // Cleanup function
    return () => {
      clearTimeout(initialSync);
      if (minuteTimer) clearInterval(minuteTimer);
    };
  }, []); // Empty dependency array = run only on mount/unmount

  // Show taskbar on dashboard routes (home and modal routes)
  const dashboardRoutes = ['/', '/en', '/om-meg', '/ferdigheter', '/prosjekter', '/tjenester', '/kontakt', '/en/about', '/en/skills', '/en/projects','/en/contact'];

  if (!dashboardRoutes.includes(location.pathname)) {
    return null;
  }

  const handleItemClick = (windowId: string, restoreFunc: () => void) => {
    setActiveItem(windowId);
    restoreFunc();

    // Reset active state after animation completes
    setTimeout(() => {
      setActiveItem(null);
    }, 300);
  };

  return (
    <TaskbarContainer>
      <TaskbarItems>
        <AnimatePresence mode="popLayout">
          {minimizedWindows.map(window => (
            <TaskbarItem
              key={window.id}
              $color={window.color}
              $active={activeItem === window.id}
              onClick={() => handleItemClick(window.id, window.restore)}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
              transition={{
                type: 'spring',
                stiffness: 400,
                damping: 30,
                mass: 0.6,
              }}
              whileHover={{
                y: -2,
                boxShadow: '0 0 10px rgba(31, 207, 94, 0.3)',
              }}
              whileTap={{ scale: 0.98 }}
              layout
            >
              {window.icon && <div className="icon-container">{window.icon}</div>}
              <div className="title">{window.header}</div>
            </TaskbarItem>
          ))}
        </AnimatePresence>
      </TaskbarItems>
      <Clock>
        <div>
          <div className="time">{time}</div>
          <div className="date">{date}</div>
        </div>
      </Clock>
    </TaskbarContainer>
  );
};

export default Taskbar;
